import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Tooltip } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import format from 'string-template';

import { ReactComponent as ViewIcon } from '../../../../../assets/icons/v2/arrow-right.svg';
import { SECTION_SLA_BUSINESS_HOURS } from '../../../../../Model/CoachNotificationView';
import { DateFormat, formatRelativeTime } from '../../../../../utils/date';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import {
  ACLeadRowType,
  CompletionStatus,
  OverdueSeverity,
  SECTION_ORDER,
  SUSPECT_THRESHOLD_BUSINESS_HOURS,
  TaskCellState,
  formatOverdueDuration,
  formatDashboardCount,
  getOverdueSeverity,
} from '../../utils/acLeadUtils';

import {
  ExpandButton,
  NameCell,
  NameSubText,
  NameText,
  OverdueDuration,
  PastDueCount,
  SharedCoachIcon,
  TaskCount,
} from './styles';
import texts from './texts.json';

/** MUI's own palette carries the meaning, so the chips need no styling of their own. */
const COMPLETION_CHIP = {
  [CompletionStatus.CLEAR]: { color: 'success', label: texts.completion.clear },
  [CompletionStatus.AT_RISK]: { color: 'warning', label: texts.completion.atRisk },
  [CompletionStatus.BEHIND]: { color: 'error', label: texts.completion.behind },
};

/** Grouped so the matrix reads as one block, and so its heading can say what the numbers are. */
const COLUMN_GROUPING_MODEL = [{
  groupId: 'pastDueByTask',
  headerName: texts.columns.pastDueByTask,
  children: SECTION_ORDER.map((section) => ({ field: section })),
}];

/** The relative age of a queue's oldest item — "3mo ago" — which is not how late it is. */
const formatItemAge = (pendingSince) => formatRelativeTime(moment.unix(pendingSince.seconds));

/**
 * What a task cell's hover says: the pending count, the past-due count, the item's age, the SLA it
 * was graded against, and how far past that it is.
 *
 * All five used to be crammed into the stacked cell this replaced, which is why it needed a whole
 * column to itself and still only showed two of them. They cost no width here.
 */
const buildTaskCellTooltip = (cell, isACRow) => {
  const slaHours = SECTION_SLA_BUSINESS_HOURS[cell.section];
  // Named on an AC row because the cell links at one specific coach's queue — the worst-hit one.
  const name = isACRow && cell.coachName
    ? format(texts.taskCell.onCoach, { sectionName: cell.sectionName, coachName: cell.coachName })
    : cell.sectionName;

  const pastDueSummary = format(texts.taskCell.pastDue, {
    count: formatDashboardCount(cell.count ?? 0),
    pending: formatDashboardCount(cell.pendingCount),
    age: cell.pendingSince ? formatItemAge(cell.pendingSince) : texts.emptyCell,
  });
  const overdue = formatOverdueDuration(cell.overdueBusinessHours ?? 0);

  switch (cell.state) {
    case TaskCellState.NONE:
      return { name, summary: texts.taskCell.none, note: format(texts.taskCell.noneNote, { slaHours }) };
    case TaskCellState.WITHIN_SLA:
      return {
        name,
        summary: format(texts.taskCell.withinSla, { pending: formatDashboardCount(cell.pendingCount) }),
        note: format(texts.taskCell.withinSlaNote, { slaHours }),
      };
    case TaskCellState.UNKNOWN_AGE:
      return {
        name,
        summary: format(texts.taskCell.unknownAge, { pending: formatDashboardCount(cell.pendingCount) }),
        note: texts.taskCell.unknownAgeNote,
      };
    case TaskCellState.EXCLUDED:
      return { name, summary: pastDueSummary, note: texts.taskCell.excluded };
    case TaskCellState.SUSPECT:
      return { name, summary: pastDueSummary, note: format(texts.taskCell.suspect, { overdue, slaHours }) };
    default:
      return { name, summary: pastDueSummary, note: format(texts.taskCell.critical, { overdue, slaHours }) };
  }
};

/** Empty queues have nothing to open, so they get no link rather than a dead one. */
const checkHasQueue = (cell) => cell.state !== TaskCellState.NONE && !!cell.path;

/**
 * One number, carrying its own state and its own link to the queue behind it.
 *
 * The number is the section's **past-due** count, so the seven columns add up to the row's own
 * `Past due` — the stacked list this replaced showed pending counts beside a past-due total, and
 * the arithmetic never closed.
 */
const TaskCell = ({ cell, isACRow }) => {
  const { name, summary, note } = buildTaskCellTooltip(cell, isACRow);
  const hasQueue = checkHasQueue(cell);

  const label = {
    [TaskCellState.NONE]: texts.emptyCell,
    [TaskCellState.UNKNOWN_AGE]: texts.unknownCell,
  }[cell.state] ?? formatDashboardCount(cell.count ?? 0);

  return (
    <Tooltip
      arrow
      placement="top"
      title={(
        <>
          {name}
          <br />
          {summary}
          <br />
          {note}
        </>
      )}
    >
      <TaskCount
        $state={cell.state}
        {...(hasQueue && {
          as: 'a',
          href: cell.path,
          target: '_blank',
          rel: 'noreferrer',
        })}
      >
        {label}
      </TaskCount>
    </Tooltip>
  );
};

TaskCell.propTypes = {
  cell: PropTypes.object.isRequired,
  isACRow: PropTypes.bool.isRequired,
};

/**
 * Flags an AC row whose numbers overlap another AC's, rather than quietly correcting them: which
 * AC should own a coach both are assigned is a staffing question this dashboard cannot answer.
 *
 * Focusable and titled, because this warning lives nowhere else on the row. MUI renders an untitled
 * SvgIcon `aria-hidden`, so without both the only notice that a row is double-counted would be a
 * hover that keyboard and screen-reader users never reach.
 */
const SharedCoachWarning = ({ coachNames }) => {
  const warning = format(texts.sharedCoach, { coachNames: coachNames.join(', ') });

  return (
    <Tooltip arrow placement="top-start" title={warning}>
      <SharedCoachIcon titleAccess={warning} tabIndex={0} />
    </Tooltip>
  );
};

SharedCoachWarning.propTypes = {
  coachNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/** A row's cell for one section. Every row carries all seven, so a column never disappears. */
const getRowTaskCell = (row, section) => row.taskCells?.find((cell) => cell.section === section);

/**
 * One column per task type, fixed.
 *
 * They stay in place even when a section is empty for everyone: a column that appears and
 * disappears as the lead filters is harder to read than a column of dashes, and the point of the
 * matrix is that the same task is always in the same place.
 */
const getSectionColumns = () => SECTION_ORDER.map((section) => ({
  field: section,
  type: 'number',
  headerName: texts.columns.sections[section],
  flex: 0.6,
  minWidth: 68,
  // The grid right-aligns number columns by default; every column here reads from the start.
  align: 'left',
  headerAlign: 'left',
  /*
   * Sorting is handled by sortACSummaries, but the grid still wants a value for the field — and it
   * has to be the counted one, so an excluded cell's struck-through number never orders a row.
   */
  valueGetter: ({ row }) => {
    const cell = getRowTaskCell(row, section);

    return cell?.isCounted ? cell.count ?? 0 : 0;
  },
  renderCell: ({ row }) => {
    const cell = getRowTaskCell(row, section);

    return cell ? <TaskCell cell={cell} isACRow={row.rowType === ACLeadRowType.AC} /> : texts.emptyCell;
  },
}));

/*
 * The grid does not sort its own rows — `sortingMode="server"` on the table hands the model to
 * `sortACSummaries` instead. The rows are flat but grouped, a coach row belonging to the AC row
 * above it, so reordering them directly would strip coach rows away from their AC; sorting the
 * summaries and re-flattening keeps the grouping intact.
 *
 * `actions` stays unsortable because it has no order to put it in. Everything else matches the
 * fields `SUMMARY_COMPARATORS` knows how to compare — the default remains most past due first,
 * ties broken by the longest wait.
 */
const getColumns = ({ expandedACIds, onToggleAC, onSlackClick }) => [
  {
    field: 'acName',
    type: 'string',
    headerName: texts.columns.assistantCoach,
    flex: 1.4,
    minWidth: 160,
    // The coach and task behind an expanded row are named in the Coaches column, so this one
    // stays the AC's own.
    renderCell: ({ row }) => (row.rowType !== ACLeadRowType.AC ? '' : (
      <NameCell>
        <Tooltip
          arrow
          placement="top-start"
          title={format(expandedACIds.has(row.acId) ? texts.collapseCoaches : texts.expandCoaches, {
            count: row.coachCount,
          })}
        >
          <ExpandButton size="small" onClick={() => onToggleAC(row.acId)}>
            {expandedACIds.has(row.acId) ? <ExpandLess /> : <ExpandMore />}
          </ExpandButton>
        </Tooltip>
        <NameText title={row.acName}>{row.acName}</NameText>
        <NameSubText>
          {format(row.coachCount === 1 ? texts.coachCount : texts.coachCountPlural, { count: row.coachCount })}
        </NameSubText>
        {row.sharedCoachNames.length > 0 && <SharedCoachWarning coachNames={row.sharedCoachNames} />}
      </NameCell>
    )),
  },
  {
    field: 'coachCount',
    headerName: texts.columns.coaches,
    flex: 1,
    minWidth: 110,
    // An AC row totals their coaches; a coach row names the one it stands for, and the pod on it.
    renderCell: ({ row }) => (row.rowType === ACLeadRowType.AC ? row.coachCount : (
      <NameText $isSubRow title={row.coachName}>
        {row.podName ? format(texts.coachWithPod, { coachName: row.coachName, podName: row.podName }) : row.coachName}
      </NameText>
    )),
  },
  ...getSectionColumns(),
  {
    field: 'totalUnread',
    type: 'number',
    headerName: texts.columns.totalUnread,
    flex: 0.8,
    minWidth: 90,
    align: 'left',
    headerAlign: 'left',
  },
  {
    field: 'pastDueCount',
    type: 'number',
    headerName: texts.columns.pastDue,
    flex: 0.8,
    minWidth: 84,
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row }) => (
      <PastDueCount $isPastDue={row.pastDueCount > 0}>{formatDashboardCount(row.pastDueCount)}</PastDueCount>
    ),
  },
  {
    field: 'worstOverdueHours',
    headerName: texts.columns.overdue,
    flex: 0.8,
    minWidth: 90,
    /*
     * How far past SLA the worst wait is — not how old the item is. Those are two different clocks,
     * and showing the age while filtering on the breach is what let a row read "5y" beside a
     * threshold grading it in business hours past a 24-hour allowance. The age and the date it
     * started move into the tooltip, where they answer "since when" without being read as "how
     * late".
     */
    renderCell: ({ row }) => (row.hasWorstOverdue ? (
      <Tooltip
        arrow
        placement="top-start"
        title={(
          <>
            {format(texts.overdueTooltip.inScope, { overdue: formatOverdueDuration(row.worstOverdueHours) })}
            <br />
            {format(texts.overdueTooltip.totalAge, {
              age: row.worstOverdueSince ? formatItemAge(row.worstOverdueSince) : texts.emptyCell,
              since: row.worstOverdueSince
                ? moment.unix(row.worstOverdueSince.seconds).format(DateFormat.DATE_MONTH_YEAR_TIME_12H_FORMAT)
                : texts.emptyCell,
            })}
          </>
        )}
      >
        {/*
          * Takes the click to the queue the worst wait is sitting in, rather than only naming it in
          * the tooltip — the cell reads as a link because it is one.
          */}
        <OverdueDuration
          $isSuspect={row.worstOverdueHours > SUSPECT_THRESHOLD_BUSINESS_HOURS}
          $isCritical={getOverdueSeverity(row.worstOverdueHours, row.worstSection) === OverdueSeverity.CRITICAL}
          {...(row.worstPath && {
            component: 'a',
            href: row.worstPath,
            target: '_blank',
            rel: 'noreferrer',
          })}
        >
          {formatOverdueDuration(row.worstOverdueHours)}
        </OverdueDuration>
      </Tooltip>
    ) : texts.emptyCell),
  },
  {
    field: 'completionStatus',
    headerName: texts.columns.completion,
    flex: 0.9,
    minWidth: 100,
    renderCell: ({ row }) => {
      if (row.rowType !== ACLeadRowType.AC) {
        return '';
      }

      const { color, label } = COMPLETION_CHIP[row.completionStatus];

      /*
       * The grade alone. The count is already in the Past due column immediately to the left, so
       * repeating it here made every row read `Behind · 5` beside a `5`.
       */
      return (
        <Chip
          size="small"
          color={color}
          variant="outlined"
          label={label}
        />
      );
    },
  },
  {
    field: 'actions',
    headerName: texts.columns.actions,
    sortable: false,
    filterable: false,
    flex: 1.1,
    minWidth: 140,
    renderCell: ({ row }) => {
      // The same button the AC dashboard offers on a coach, so the action reads identically in both.
      if (row.rowType === ACLeadRowType.COACH) {
        return (
          <PrimaryButton
            onClick={() => window.open(`/c/${row.coachId}`, '_blank')}
            icon={<ViewIcon />}
            variant="info"
            size="medium"
          >
            {texts.buttons.viewDashboard}
          </PrimaryButton>
        );
      }

      // Chasing is per person, so the follow-up sits on the AC row and nowhere else.
      if (row.pastDueCount === 0) {
        return '';
      }

      return row.acSlackId ? (
        <Tooltip arrow placement="top-start" title={texts.chase.slackTooltip}>
          {/* Tooltip has to hang its ref on something, and PrimaryButton does not forward one. */}
          <span>
            <PrimaryButton onClick={() => onSlackClick(row.summary)} variant="info" size="medium">
              {texts.chase.slack}
            </PrimaryButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip arrow placement="top-start" title={format(texts.chase.noSlack, { acName: row.acName })}>
          <span>{texts.emptyCell}</span>
        </Tooltip>
      );
    },
  },
];

export default getColumns;
export { COLUMN_GROUPING_MODEL };
